import React, { useEffect, useState, useRef } from 'react';
import Maplibre from 'react-map-gl';
import { Marker, ScaleControl, Popup } from 'react-map-gl';
import { simplify } from '@turf/simplify';
import { bbox } from '@turf/bbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import { updateLayerOrder } from '../../common/MapTools';

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const TagsTrackingMap = React.memo(({
  startMarkerCoordinates,
  endMarkerCoordinates,
  contentData,
  contentRunSelected,
  showHistory,
  mapStyle,
  MAPBOX_ACCESS_TOKEN,
  mapRef,
  isDesktop,
}) => {

  const [markersCoordinates, setMarkersCoordinates] = useState({
    currentMarker1: [],
    currentMarker2: [],
  });

  const [popupInfo, setPopupInfo] = useState(null);

  const previousCoordinates = useRef(null);
  const previousBearing = useRef(0);
  const markersCoordinatesRef = useRef(markersCoordinates);

  useEffect(() => {
    markersCoordinatesRef.current = markersCoordinates;
  }, [markersCoordinates]);

  const updateCamera = () => {
    if (!showHistory) return;
    if (!previousCoordinates.current)
      previousCoordinates.current = markersCoordinates.currentMarker1;

    const map = mapRef.current.getMap();
    const offsetDistance = 0.000001;

    const currentMarker = markersCoordinatesRef.current.currentMarker1;

    if (currentMarker.length > 0) {
      const deltaLng = currentMarker[0] - previousCoordinates.current[0];
      const deltaLat = currentMarker[1] - previousCoordinates.current[1];

      let bearing = previousBearing.current;

      if (Math.abs(deltaLng) > 1e-6 || Math.abs(deltaLat) > 1e-6) {
        // Calculate new bearing
        bearing = (Math.atan2(deltaLng, deltaLat) * 180) / Math.PI;
        bearing = (bearing + 360) % 360;

        // Smooth out the bearing transition
        if (previousBearing.current !== null) {
          let bearingDiff = bearing - previousBearing.current;
          if (bearingDiff > 180) bearingDiff -= 360;
          else if (bearingDiff < -180) bearingDiff += 360;

          bearing = previousBearing.current + bearingDiff;
        }
      }

      // Update the camera's position behind the user
      const radians = (bearing * Math.PI) / 180;
      const cameraLng = currentMarker[0] - offsetDistance * Math.sin(radians);
      const cameraLat = currentMarker[1] - offsetDistance * Math.cos(radians);

      // Move the camera smoothly to the new position and bearing
      map.easeTo({
        center: [cameraLng, cameraLat],
        bearing: bearing,
        pitch: 80,
        zoom: 22,
        duration: 200,
        easing: (t) => t,
      });

      // Update the previous coordinates and bearing
      previousCoordinates.current = [currentMarker[0], currentMarker[1]];
      previousBearing.current = bearing;
    }
  };


  const addRouteLayer = (geojsonRoute, mapDataRoute, showHistory) => {
    let map = mapRef.current.getMap();
    if (mapRef.current) {
      if (map.getLayer('route')) {
        map.removeLayer('route');
      }
      if (map.getSource('route')) {
        map.removeSource('route');
      }

      if (showHistory == undefined)
        showHistory = true;

      if (showHistory) {
        // Add the route layer if history is enabled
        map.addSource('route', {
          type: 'geojson',
          lineMetrics: true,
          data: geojsonRoute
        });

        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#00f',
            'line-width': 5,
          },
        });

        const bboxBounds = bbox(geojsonRoute.features[0].geometry);
        const bounds2 = [
          [
            bboxBounds[0],
            bboxBounds[1]
          ],
          [
            bboxBounds[2],
            bboxBounds[3]
          ]
        ];

        map.fitBounds(bounds2, {
          padding: isDesktop ? 100 : 30,
          maxZoom: 17,  // Set the maximum zoom level to 16
          duration: 1000,
        });
      } else if (mapDataRoute?.length) {
        // Only show the last point if no history
        const lastPoint = mapDataRoute[mapDataRoute.length - 1];
        map.easeTo({
          center: lastPoint,
          zoom: 19,
          duration: 1000,
        });
      } else {
        removeLayers();
      }
    }
  };

  const removeLayers = () => {
    let map = mapRef.current.getMap();
    if (map.getLayer('route')) {
      map.removeLayer('route');
    }
    if (map.getSource('route')) {
      map.removeSource('route');
    }
  };

  useEffect(() => {

    if (mapRef.current && contentData.length > 0) {
      let map = mapRef.current.getMap();

      const firstItemIndex = 0;
      let mapDataRoute;

      mapDataRoute = contentData
        .slice(firstItemIndex)
        .map((item) => [item.lon, item.lat]);

      const geojsonRoute = {
        'type': 'FeatureCollection',
        'features': [{
          'type': 'Feature',
          'geometry': {
            'type': 'LineString',
            'coordinates': mapDataRoute
          }
        }]
      };

      // Simplify the route for optimization
      try {
        if (geojsonRoute.features[0].geometry.data) {
          const newGeojsonRoute = simplify(geojsonRoute.features[0].geometry.data, { tolerance: 0.0000001, highQuality: true });
          geojsonRoute.features[0].geometry.data = newGeojsonRoute.geometry;
        }
      } catch (error) {
        console.error(error);
      }

      map.once('idle', () => {
        addRouteLayer(geojsonRoute, mapDataRoute, showHistory);
      });

      addRouteLayer(geojsonRoute, mapDataRoute, showHistory);
    } else if (mapRef.current) {
      removeLayers();
    }

    // Return a cleanup function to remove the event listener
    return () => { };
  }, [contentData, mapStyle, contentRunSelected, showHistory]);


  return (
    <Maplibre
      initialViewState={{
        latitude: 48,
        longitude: 1,
        zoom: 4,
        container: 'map-container',
        attributionControl: false,
      }}
      style={{ width: '100%', height: '100%' }}
      mapStyle={mapStyle}
      mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
      ref={mapRef}
      container='map'
      cooperativeGestures={isDesktop ? false : true}
    >
      <ScaleControl />

      {/* Markers for the route */}
      {showHistory && contentData.map((point, index) => {
        const maxRadius = 9;
        const minRadius = 3;
        const confClamped = Math.max(80, Math.min(point.conf, 150));
        const confRange = (150 - confClamped) / 70; // Conf from 100 to 200 mapped to 1 to 0
        const radius = minRadius + confRange * (maxRadius - minRadius);

        let pointColor = "red";
        let zIndex = 100;

        if (index == 0) {
          pointColor = "green";
          zIndex = 200;
        }
        else if (index == contentData.length - 1) {
          pointColor = "black";
          zIndex = 200;
        }
        return (
          <Marker key={index} longitude={point.lon} latitude={point.lat} anchor='center'>
            <div
              onMouseEnter={() => setPopupInfo(point)}
              onMouseLeave={() => setPopupInfo(null)}
              onClick={() => setPopupInfo(point)}
              style={{ cursor: 'pointer' }}  // Change the cursor style here
            >
              <svg height="20" width="20" style={{ zIndex: zIndex }}>
                <circle cx="10" cy="10" r={radius} fill={pointColor} stroke="white" strokeWidth="2" />
              </svg>
            </div>
          </Marker>
        );

      })}

      {
        popupInfo && (
          <Popup
            longitude={popupInfo.lon}
            latitude={popupInfo.lat}
            onClose={() => setPopupInfo(null)}
            closeOnClick={false}
            anchor='top'
            closeButton={false}
            style={{
              color: 'white',
            }}
          >
            <div style={{ color: 'black', padding: '5px' }}>
              <div><b>{`${new Date(popupInfo.timestamp * 1000).toLocaleString()}`}</b></div>
              <div>{`Confidence: ${popupInfo.conf}`}</div>
            </div>
          </Popup>
        )
      }

      {
        !showHistory && endMarkerCoordinates.length > 0 && (
          <Marker longitude={endMarkerCoordinates[0]} latitude={endMarkerCoordinates[1]} anchor='bottom'>
            <i className='fa fa-map-marker-alt marker-icon' style={{ color: '#000' }}></i>
          </Marker>
        )
      }

    </Maplibre >
  );
})

export default React.memo(TagsTrackingMap);
